import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import {
  User,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider
} from 'firebase/auth';
import { auth } from '../firebase';
import { DeviceInfoService } from '../services/deviceInfoService';
import { EmailAuthService } from '../services/emailAuthService';
import { PasscodeVerificationService } from '../services/passcodeVerificationService';
import { sessionService } from '../services/sessionService';
import { marketingService } from '../services/marketingService';
import { subscriptionService } from '../services/subscriptionService';

interface AuthContextType {
  currentUser: User | null;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  loading: boolean;
  isNewUser: boolean;
  // Email verification methods
  sendEmailVerification: () => Promise<{ success: boolean; error?: string }>;
  sendCustomEmailVerification: () => Promise<{ success: boolean; error?: string }>;
  verifyEmailWithCode: (actionCode: string) => Promise<{ success: boolean; error?: string }>;
  // Passcode verification methods
  sendEmailVerificationPasscode: (forceNew?: boolean) => Promise<{ success: boolean; error?: string }>;
  verifyEmailWithPasscode: (passcode: string) => Promise<{ success: boolean; error?: string }>;
  // Password reset methods
  sendPasswordResetEmail: (email: string) => Promise<{ success: boolean; error?: string }>;
  sendCustomPasswordResetEmail: (email: string) => Promise<{ success: boolean; error?: string }>;
  sendPasswordResetPasscode: (email: string, userName?: string) => Promise<{ success: boolean; error?: string }>;
  verifyPasswordResetPasscode: (email: string, passcode: string) => Promise<{ valid: boolean; error?: string }>;
  resetPasswordWithPasscode: (email: string, passcode: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  confirmPasswordReset: (actionCode: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
  checkActionCode: (actionCode: string) => Promise<{ valid: boolean; operation?: string; email?: string; error?: string }>;
  // Email update methods
  updateEmailWithVerification: (newEmail: string) => Promise<{ success: boolean; error?: string }>;
  // Custom email verification methods
  isEmailVerifiedCustom: () => Promise<boolean>;
  // Utility methods
  resetNewUserFlag: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  console.log('📍 [TRACE] AuthProvider: Rendering');
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);

  const signup = async (email: string, password: string) => {
    console.log('🚀 Starting signup process for:', email);
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    console.log('🎉 User signup successful');
    console.log('👤 Created user UID:', userCredential.user.uid);

    // Mark as new user for email verification requirement
    setIsNewUser(true);

    // Device info, marketing, and verification email are deferred so the UI can navigate
    // to /verify-email immediately after account creation (verification sends from that flow).
    void (async () => {
      try {
        const deviceInfo = await DeviceInfoService.collectDeviceInfo();
        await DeviceInfoService.updateUserDeviceInfo(userCredential.user.uid, deviceInfo);
        console.log('✅ Device info collected and saved for new user');
      } catch (error) {
        console.warn('❌ Failed to collect device information during signup:', error);
      }
    })();

    void (async () => {
      try {
        const signupSource = sessionStorage.getItem('signupSource');
        if (signupSource) {
          console.log('📢 Recording marketing signup source:', signupSource);
          await marketingService.recordSignup(signupSource, userCredential.user.uid);
          sessionStorage.removeItem('signupSource');
          console.log('✅ Marketing source recorded successfully');
        }
      } catch (error) {
        console.warn('❌ Failed to record marketing source during signup:', error);
      }
    })();

    void (async () => {
      try {
        await subscriptionService.ensureStarterSubscription(userCredential.user.uid);
        console.log('✅ Starter subscription ensured for new user');
      } catch (error) {
        console.warn('❌ Failed to ensure starter subscription during signup:', error);
      }
    })();
  };

  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log('🔑 User login successful, collecting device info...');

    // Mark as existing user (no email verification required)
    setIsNewUser(false);
    console.log('👤 Login successful, isNewUser set to false');

    // Collect device information after successful login
    try {
      const deviceInfo = await DeviceInfoService.collectDeviceInfo();
      await DeviceInfoService.updateUserDeviceInfo(userCredential.user.uid, deviceInfo);
      console.log('✅ Device info collected and saved for existing user');

      // Create a new session
      const sessionToken = sessionService.generateSessionToken();
      console.log('🔑 Creating session with token:', sessionToken);
      console.log('📱 Device info:', deviceInfo);

      const sessionId = await sessionService.createSession(
        userCredential.user.uid,
        {
          browser: deviceInfo.browser,
          os: deviceInfo.operatingSystem,
          device: deviceInfo.deviceType,
          userAgent: deviceInfo.userAgent
        },
        {
          ip: deviceInfo.ipAddress || 'Unknown',
          city: deviceInfo.city,
          country: deviceInfo.country
        },
        sessionToken
      );

      // Store current session ID
      sessionService.setCurrentSessionId(sessionId);
      console.log('✅ Session created successfully with ID:', sessionId);
    } catch (error) {
      console.warn('❌ Failed to collect device information or create session during login:', error);
      // Don't throw error as login should still succeed
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    console.log('🔑 Google sign-in successful, collecting device info...');

    // Check if this is a new user by checking if they have a display name
    // (Google automatically fills this for new users)
    const isNew = !result.user.metadata.creationTime ||
      new Date(result.user.metadata.creationTime).getTime() > Date.now() - 10000;
    setIsNewUser(isNew);

    // Google accounts are already verified by Google, so mark as verified automatically
    if (isNew) {
      console.log('✅ Marking Google user email as verified (Google accounts are pre-verified)');
      try {
        await PasscodeVerificationService.markEmailAsVerified(result.user.uid, result.user.email || '', 'google');
        console.log('✅ Email verification status set for Google user');
      } catch (error) {
        console.warn('❌ Failed to mark Google user email as verified:', error);
        // Don't throw error, continue with sign-in
      }

      // Record marketing source if user signed up via a marketing link
      try {
        const signupSource = sessionStorage.getItem('signupSource');
        if (signupSource) {
          console.log('📢 Recording marketing signup source for Google user:', signupSource);
          await marketingService.recordSignup(signupSource, result.user.uid);
          sessionStorage.removeItem('signupSource');
          console.log('✅ Marketing source recorded for Google user');
        }
      } catch (error) {
        console.warn('❌ Failed to record marketing source during Google signup:', error);
      }

      try {
        await subscriptionService.ensureStarterSubscription(result.user.uid);
        console.log('✅ Starter subscription ensured for new Google user');
      } catch (error) {
        console.warn('❌ Failed to ensure starter subscription during Google signup:', error);
      }
    }

    // Collect device information
    try {
      const deviceInfo = await DeviceInfoService.collectDeviceInfo();
      await DeviceInfoService.updateUserDeviceInfo(result.user.uid, deviceInfo);
      console.log('✅ Device info collected and saved');

      // Create a session for existing users (skip for brand new users)
      if (!isNew) {
        const sessionToken = sessionService.generateSessionToken();
        const sessionId = await sessionService.createSession(
          result.user.uid,
          {
            browser: deviceInfo.browser,
            os: deviceInfo.operatingSystem,
            device: deviceInfo.deviceType,
            userAgent: deviceInfo.userAgent
          },
          {
            ip: deviceInfo.ipAddress || 'Unknown',
            city: deviceInfo.city,
            country: deviceInfo.country
          },
          sessionToken
        );
        sessionService.setCurrentSessionId(sessionId);
        console.log('✅ Session created successfully');
      }
    } catch (error) {
      console.warn('❌ Failed to collect device information or create session:', error);
    }
  };

  const logout = async () => {
    // Deactivate current session before logout
    try {
      const currentSessionId = sessionService.getCurrentSessionId();
      if (currentSessionId) {
        await sessionService.deactivateSession(currentSessionId);
        sessionService.clearCurrentSessionId();
      }
    } catch (error) {
      console.warn('❌ Failed to deactivate session during logout:', error);
    }

    await signOut(auth);
    setIsNewUser(false); // Reset new user flag on logout
  };

  // Email verification methods
  const sendEmailVerification = async (): Promise<{ success: boolean; error?: string }> => {
    if (!currentUser) {
      return { success: false, error: 'No user logged in' };
    }
    return await EmailAuthService.sendVerificationEmail(currentUser);
  };

  const sendCustomEmailVerification = async (): Promise<{ success: boolean; error?: string }> => {
    if (!currentUser) {
      return { success: false, error: 'No user logged in' };
    }
    // Generate verification link - in a real app, you'd get this from Firebase
    const verificationLink = `${window.location.origin}/verify-email?actionCode=VERIFY_EMAIL&oobCode=VERIFY_CODE`;
    return await EmailAuthService.sendCustomVerificationEmail(currentUser, verificationLink);
  };

  const verifyEmailWithCode = async (actionCode: string): Promise<{ success: boolean; error?: string }> => {
    return await EmailAuthService.verifyEmailWithCode(actionCode);
  };

  // Password reset methods
  const sendPasswordResetEmail = async (email: string): Promise<{ success: boolean; error?: string }> => {
    return await EmailAuthService.sendPasswordResetEmailToUser(email);
  };

  const sendCustomPasswordResetEmail = async (email: string): Promise<{ success: boolean; error?: string }> => {
    // Generate reset link - in a real app, you'd get this from Firebase
    const resetLink = `${window.location.origin}/reset-password?actionCode=PASSWORD_RESET&oobCode=RESET_CODE`;
    return await EmailAuthService.sendCustomPasswordResetEmail(email, resetLink);
  };

  const confirmPasswordReset = async (actionCode: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    return await EmailAuthService.confirmPasswordReset(actionCode, newPassword);
  };

  const checkActionCode = async (actionCode: string): Promise<{ valid: boolean; operation?: string; email?: string; error?: string }> => {
    return await EmailAuthService.checkActionCodeValidity(actionCode);
  };

  // Passcode verification methods
  const sendEmailVerificationPasscode = async (forceNew?: boolean): Promise<{ success: boolean; error?: string }> => {
    const user = currentUser ?? auth.currentUser;
    if (!user) {
      return { success: false, error: 'No user logged in' };
    }

    try {
      // Send verification passcode using the PasscodeVerificationService
      const result = await PasscodeVerificationService.sendEmailVerificationPasscode(user, forceNew || false);
      return result;
    } catch (error) {
      console.error('Error with verification passcode:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send verification passcode'
      };
    }
  };

  const verifyEmailWithPasscode = async (passcode: string): Promise<{ success: boolean; error?: string }> => {
    const user = currentUser ?? auth.currentUser;
    if (!user) {
      return { success: false, error: 'No user logged in' };
    }
    return await PasscodeVerificationService.verifyEmailWithPasscode(user, passcode);
  };

  const sendPasswordResetPasscode = async (email: string, userName?: string): Promise<{ success: boolean; error?: string }> => {
    return await PasscodeVerificationService.sendPasswordResetPasscode(email, userName);
  };

  const verifyPasswordResetPasscode = async (email: string, passcode: string): Promise<{ valid: boolean; error?: string }> => {
    return await PasscodeVerificationService.verifyPasswordResetPasscode(email, passcode);
  };

  const resetPasswordWithPasscode = async (email: string, passcode: string, newPassword: string): Promise<{ success: boolean; error?: string }> => {
    return await EmailAuthService.resetPasswordWithPasscode(email, passcode, newPassword);
  };

  // Email update methods
  const updateEmailWithVerification = async (newEmail: string): Promise<{ success: boolean; error?: string }> => {
    if (!currentUser) {
      return { success: false, error: 'No user logged in' };
    }
    return await EmailAuthService.updateEmailWithVerification(currentUser, newEmail);
  };

  // Check custom email verification status
  const isEmailVerifiedCustom = useCallback(async (): Promise<boolean> => {
    const user = currentUser ?? auth.currentUser;
    if (!user) {
      console.log('🔍 isEmailVerifiedCustom: No current user');
      return false;
    }

    // First check custom Firestore system
    const customVerified = await PasscodeVerificationService.isEmailVerified(user.uid);
    
    if (customVerified) {
      console.log(`🔍 isEmailVerifiedCustom: Verified in custom system for ${user.uid}`);
      return true;
    }

    // Fallback: Check Firebase Auth verification status
    // If verified in Firebase but NOT in custom system, sync it back
    if (user.emailVerified) {
      console.log(`🔍 isEmailVerifiedCustom: Verified in Firebase Auth but missing custom record. Syncing...`);
      try {
        await PasscodeVerificationService.markEmailAsVerified(
          user.uid, 
          user.email || '', 
          'firebase-sync'
        );
        return true;
      } catch (error) {
        console.warn('❌ isEmailVerifiedCustom: Failed to sync verification status:', error);
        return true; // Still return true as they are verified in Firebase
      }
    }

    console.log(`🔍 isEmailVerifiedCustom: Not verified for ${user.uid}`);
    return false;
  }, [currentUser]);

  // Utility methods
  const resetNewUserFlag = () => {
    setIsNewUser(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      setLoading(false);

      // Collect device information for existing users when they're detected
      if (user) {
        console.log('🔄 Auth state changed, user detected, collecting device info...');
        try {
          const deviceInfo = await DeviceInfoService.collectDeviceInfo();
          await DeviceInfoService.updateUserDeviceInfo(user.uid, deviceInfo);
          console.log('✅ Device info collected and saved on auth state change');
        } catch (error) {
          console.warn('❌ Failed to collect device information on auth state change:', error);
        }
      } else {
        console.log('👋 User logged out');
      }
    });

    return unsubscribe;
  }, []);

  const value: AuthContextType = useMemo(() => ({
    currentUser,
    login,
    signup,
    signInWithGoogle,
    logout,
    loading,
    isNewUser,
    sendEmailVerification,
    sendCustomEmailVerification,
    verifyEmailWithCode,
    sendEmailVerificationPasscode,
    verifyEmailWithPasscode,
    sendPasswordResetEmail,
    sendCustomPasswordResetEmail,
    sendPasswordResetPasscode,
    verifyPasswordResetPasscode,
    resetPasswordWithPasscode,
    confirmPasswordReset,
    checkActionCode,
    updateEmailWithVerification,
    isEmailVerifiedCustom,
    resetNewUserFlag,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [currentUser, loading, isNewUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

